import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Card,
  CardContent,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton
} from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { updateBusinessDetails } from '../../../store/modules/generalData/actions';

function BusinessDetails({ businessDetails, dispatchUpdateBusinessDetails }) {
  // Ensure at least one capTable row on mount
  useEffect(() => {
    if (businessDetails && (!businessDetails?.capTable || businessDetails?.capTable.length === 0)) {
      handleAddRow();
    }
  }, []);

  // top‐left & top‐right simple input handlers
  const handleChange = e => {
    const { name, value } = e.target;
    dispatchUpdateBusinessDetails(name, value);
  };
  const handleRetainedChange = e => {
    const { name, value } = e.target;
    dispatchUpdateBusinessDetails(
      'retainedBy',
      { ...businessDetails?.retainedBy, [name]: value }
    );
  };

  // capTable management
  const handleAddRow = () => {
    const newRow = { shareholderNames: '', positionOrTitle: '', descriptionOfDuties: '' };
    const newTable = (businessDetails?.capTable || []).concat(newRow);
    dispatchUpdateBusinessDetails('capTable', newTable);
  };
  const handleRemoveRow = idx => {
    const newTable = businessDetails?.capTable?.filter((_, i) => i !== idx);
    dispatchUpdateBusinessDetails('capTable', newTable);
  };
  const handleCapTableChange = (idx, field) => e => {
    const val = e.target.value;
    const newTable = businessDetails?.capTable?.map((r, i) =>
      i === idx ? { ...r, [field]: val } : r
    );
    dispatchUpdateBusinessDetails('capTable', newTable);
  };

  return (
    <>
      {businessDetails ?
        (<div className="p-4 space-y-4">
          {/* TOP ROW */}
          <div className="flex flex-wrap -mx-2 items-stretch">
            {/* LEFT CARD */}
            <div className="w-full md:w-1/2 px-2 mb-4 flex">
              <Card className="flex flex-col flex-1">
                <CardContent className="flex-1 space-y-4">
                  <div>
                    <label className="block">
                      <span className="block font-semibold mb-1">
                        Estimated Number of Full-Time Employees
                      </span>
                      <input
                        type="number"
                        name="estimatedEmployees"
                        value={businessDetails?.estimatedEmployees || ''}
                        onChange={handleChange}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block font-semibold mb-1">
                        Organizational Form
                      </span>
                      <select
                        name="organizationalForm"
                        value={businessDetails?.organizationalForm || ''}
                        onChange={handleChange}
                        className="w-full border rounded px-3 py-2"
                      >
                        <option value="">Select one…</option>
                        <option value="C-Corporation">C-Corporation</option>
                        <option value="S-Corporation">S-Corporation</option>
                        <option value="Sole Proprietorship">Sole Proprietorship</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Limited Liability Company (LLC)">
                          Limited Liability Company (LLC)
                        </option>
                        <option value="Other">Other</option>
                      </select>
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block font-semibold mb-1">
                        State of Organizational Form
                      </span>
                      <textarea
                        name="stateOfOrganizationalForm"
                        rows={3}
                        value={businessDetails?.stateOfOrganizationalForm || ''}
                        onChange={handleChange}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* RIGHT CARD */}
            <div className="w-full md:w-1/2 px-2 mb-4 flex">
              <Card className="flex flex-col flex-1">
                <CardContent className="flex-1 space-y-4">
                  <h3 className="font-semibold">Retained by</h3>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Contact Name</span>
                      <input
                        type="text"
                        name="contactName"
                        value={businessDetails?.retainedBy?.contactName || ''}
                        onChange={handleRetainedChange}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Position Title</span>
                      <input
                        type="text"
                        name="positionTitle"
                        value={businessDetails?.retainedBy?.positionTitle || ''}
                        onChange={handleRetainedChange}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Contact Email</span>
                      <input
                        type="email"
                        name="contactEmail"
                        value={businessDetails?.retainedBy?.contactEmail || ''}
                        onChange={handleRetainedChange}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Contact Phone</span>
                      <input
                        type="text"
                        name="contactPhone"
                        value={businessDetails?.retainedBy?.contactPhone || ''}
                        onChange={handleRetainedChange}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* BOTTOM CARD: Cap Table */}
          <Card>
            <CardContent>
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-semibold">Business Owners Cap Table</h2>
                <Button
                  startIcon={<AddCircleOutline />}
                  color="primary"
                  onClick={handleAddRow}
                >
                  Add Row
                </Button>
              </div>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Shareholder Names</TableCell>
                    <TableCell>Position or Title</TableCell>
                    <TableCell>Description of Duties</TableCell>
                    <TableCell align="center">Remove</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businessDetails?.capTable?.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <input
                          type="text"
                          value={row.shareholderNames}
                          onChange={handleCapTableChange(idx, 'shareholderNames')}
                          className="w-full border rounded px-2 py-1"
                        />
                      </TableCell>
                      <TableCell>
                        <input
                          type="text"
                          value={row.positionOrTitle}
                          onChange={handleCapTableChange(idx, 'positionOrTitle')}
                          className="w-full border rounded px-2 py-1"
                        />
                      </TableCell>
                      <TableCell>
                        <input
                          type="text"
                          value={row.descriptionOfDuties}
                          onChange={handleCapTableChange(idx, 'descriptionOfDuties')}
                          className="w-full border rounded px-2 py-1"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleRemoveRow(idx)}>
                          <RemoveCircleOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>)
        :
        (<div/>)
      }
    </>
  );
}

const mapStateToProps = state => ({
  businessDetails: state.generalData.businessDetails
});
const mapDispatchToProps = dispatch => ({
  async dispatchUpdateBusinessDetails(name, value) {
    await dispatch(updateBusinessDetails(name, value))
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(BusinessDetails);